import { useState } from 'react';

import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

import { createPortal } from 'react-dom';

import '../../chaptercontainer.css';
import './ClassificationOption.css';


const normalizeValue = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase();


/* =====================================================
   DRAGGABLE OPTION
   ===================================================== */

const DraggableOption = ({
  option,
  disabled = false,
  className,
  onClick,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useDraggable({
    id: String(option),

    disabled,

    data: {
      option,
    },
  });


  const style = {
    opacity: isDragging ? 0 : 1,
  };


  return (
    <button
      ref={setNodeRef}
      type="button"
      className={className}
      style={style}
      disabled={disabled}
      onClick={onClick}
      {...listeners}
      {...attributes}
    >
      {String(option)}
    </button>
  );
};


/* =====================================================
   DROPPABLE CATEGORY
   ===================================================== */

const DroppableCategory = ({
  category,
  children,
}) => {
  const {
    isOver,
    setNodeRef,
  } = useDroppable({
    id: `category-${category.label}`,

    data: {
      categoryLabel:
        category.label,
    },
  });


  return (
    <div
      ref={setNodeRef}
      className={[
        'classification-column-bc',

        isOver
          ? 'classification-column-active-bc'
          : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <h3 className="classification-column-title-bc">
        {category.label}
      </h3>

      <div className="classification-column-content-bc">
        {children}
      </div>
    </div>
  );
};


/* =====================================================
   CLASSIFICATION OPTION
   ===================================================== */

const ClassificationOption = ({
  question,

  categories = [],
  options = [],
  answers = {},

  isAnswerChecked = false,

  onAnswersChange,

  showQuestion = true,
  showContext = true,

  useScrollWrapper = true,
}) => {
  const [
    activeOption,
    setActiveOption,
  ] = useState(null);


  const sensors = useSensors(
    useSensor(
      PointerSensor,
      {
        activationConstraint: {
          distance: 6,
        },
      }
    ),

    useSensor(
      TouchSensor,
      {
        activationConstraint: {
          delay: 150,
          tolerance: 8,
        },
      }
    )
  );


  if (!question) {
    return null;
  }


  /* =====================================================
     SAFE DATA
     ===================================================== */

  const safeCategories =
    Array.isArray(categories)
      ? categories
      : [];


  const safeOptions =
    Array.isArray(options)
      ? options
      : [];


  const safeAnswers =
    answers &&
    typeof answers === 'object' &&
    !Array.isArray(answers)
      ? answers
      : {};


  /* =====================================================
     REMOVE OPTION FROM ALL CATEGORIES
     ===================================================== */

  const removeOptionFromAllCategories = (
    currentAnswers,
    option
  ) => {
    const nextAnswers = {};


    safeCategories.forEach(
      (category) => {
        const categoryLabel =
          category.label;


        const currentCategoryAnswers =
          Array.isArray(
            currentAnswers[
              categoryLabel
            ]
          )
            ? currentAnswers[
                categoryLabel
              ]
            : [];


        nextAnswers[
          categoryLabel
        ] =
          currentCategoryAnswers.filter(
            (answer) =>
              normalizeValue(
                answer
              ) !==
              normalizeValue(
                option
              )
          );
      }
    );


    return nextAnswers;
  };


  /* =====================================================
     PLACE OPTION
     ===================================================== */

  const placeOptionInCategory = (
    categoryLabel,
    option
  ) => {
    if (
      isAnswerChecked ||
      !categoryLabel ||
      !option
    ) {
      return;
    }


    const cleanedAnswers =
      removeOptionFromAllCategories(
        safeAnswers,
        option
      );


    const currentCategoryAnswers =
      Array.isArray(
        cleanedAnswers[
          categoryLabel
        ]
      )
        ? cleanedAnswers[
            categoryLabel
          ]
        : [];


    onAnswersChange?.({
      ...cleanedAnswers,

      [categoryLabel]: [
        ...currentCategoryAnswers,
        option,
      ],
    });
  };


  /* =====================================================
     REMOVE OPTION FROM CATEGORY
     ===================================================== */

  const removeOptionFromCategory = (
    categoryLabel,
    option
  ) => {
    if (isAnswerChecked) {
      return;
    }


    const currentCategoryAnswers =
      Array.isArray(
        safeAnswers[
          categoryLabel
        ]
      )
        ? safeAnswers[
            categoryLabel
          ]
        : [];


    onAnswersChange?.({
      ...safeAnswers,

      [categoryLabel]:
        currentCategoryAnswers.filter(
          (answer) =>
            normalizeValue(
              answer
            ) !==
            normalizeValue(
              option
            )
        ),
    });
  };


  /* =====================================================
     DRAG EVENTS
     ===================================================== */

  const handleDragStart = (
    event
  ) => {
    const draggedOption =
      event.active.data.current
        ?.option ??
      event.active.id;


    setActiveOption(
      draggedOption
    );
  };


  const handleDragCancel = () => {
    setActiveOption(null);
  };


  const handleDragEnd = (
    event
  ) => {
    const {
      active,
      over,
    } = event;


    setActiveOption(null);


    if (
      !over ||
      isAnswerChecked
    ) {
      return;
    }


    const option =
      active.data.current
        ?.option ??
      active.id;


    const categoryLabel =
      over.data.current
        ?.categoryLabel;


    if (!categoryLabel) {
      return;
    }


    placeOptionInCategory(
      categoryLabel,
      option
    );
  };


  /* =====================================================
     AVAILABLE OPTIONS
     ===================================================== */

  const usedOptions =
    Object.values(
      safeAnswers
    )
      .filter(Array.isArray)
      .flat();


  const availableOptions =
    safeOptions.filter(
      (option) =>
        !usedOptions.some(
          (usedOption) =>
            normalizeValue(
              usedOption
            ) ===
            normalizeValue(
              option
            )
        )
    );


  /* =====================================================
     OPTION CLASS
     ===================================================== */

  const getOptionClassName = (
    categoryLabel,
    option
  ) => {
    const classes = [
      'classification-placed-option-bc',
    ];


    if (isAnswerChecked) {
      const expectedAnswers =
        Array.isArray(
          question.correctAnswer?.[
            categoryLabel
          ]
        )
          ? question.correctAnswer[
              categoryLabel
            ]
          : [];


      const isCorrect =
        expectedAnswers.some(
          (
            expectedAnswer
          ) =>
            normalizeValue(
              expectedAnswer
            ) ===
            normalizeValue(
              option
            )
        );


      classes.push(
        isCorrect
          ? 'classification-option-correct-bc'
          : 'classification-option-incorrect-bc'
      );
    }


    return classes.join(' ');
  };


  /* =====================================================
     CONTENT
     ===================================================== */

  const content = (
    <div className="classification-option-bc">

      {showContext &&
        question.context && (
          <div className="classification-context-bc">
            <p>
              {question.context}
            </p>
          </div>
        )}


      {showQuestion &&
        question.question && (
          <div className="classification-question-bc">
            <p>
              {question.question}
            </p>
          </div>
        )}


      <div className="classification-columns-wrapper-bc">

        <div className="classification-columns-bc">

          {safeCategories.map(
            (
              category,
              index
            ) => {
              const categoryLabel =
                category.label;


              const categoryAnswers =
                Array.isArray(
                  safeAnswers[
                    categoryLabel
                  ]
                )
                  ? safeAnswers[
                      categoryLabel
                    ]
                  : [];


              return (
                <DroppableCategory
                  key={`${categoryLabel}-${index}`}
                  category={
                    category
                  }
                >

                  {categoryAnswers.map(
                    (
                      option,
                      optionIndex
                    ) => (
                      <DraggableOption
                        key={`${option}-${optionIndex}`}
                        option={
                          option
                        }
                        disabled={
                          isAnswerChecked
                        }
                        className={
                          getOptionClassName(
                            categoryLabel,
                            option
                          )
                        }
                        onClick={() =>
                          removeOptionFromCategory(
                            categoryLabel,
                            option
                          )
                        }
                      />
                    )
                  )}

                </DroppableCategory>
              );
            }
          )}

        </div>

      </div>


      <div
        className={[
          'classification-options-pool-bc',

          isAnswerChecked
            ? 'classification-options-pool-hidden-bc'
            : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >

        {availableOptions.map(
          (
            option,
            index
          ) => (
            <DraggableOption
              key={`${option}-${index}`}
              option={option}
              disabled={
                isAnswerChecked
              }
              className="classification-option-item-bc"
            />
          )
        )}

      </div>

    </div>
  );


  /* =====================================================
     RENDER
     ===================================================== */

  return (
    <DndContext
      sensors={sensors}
      onDragStart={
        handleDragStart
      }
      onDragEnd={
        handleDragEnd
      }
      onDragCancel={
        handleDragCancel
      }
    >

      {useScrollWrapper ? (
        <div className="quiz-content-scroll-bc">
          {content}
        </div>
      ) : (
        content
      )}


      {typeof document !==
        'undefined' &&
        createPortal(
          <DragOverlay
            adjustScale={
              false
            }
            dropAnimation={
              null
            }
          >
            {activeOption ? (
              <div className="classification-drag-overlay-bc">
                {String(
                  activeOption
                )}
              </div>
            ) : null}
          </DragOverlay>,

          document.body
        )}

    </DndContext>
  );
};


export default ClassificationOption;